import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  buildSearchString,
  parseSearchParams,
  updateSearchParams,
} from "tap/utils/urlParams";
import { gridStateToParams } from "tap/components/AGGrid/gridStateParams";
import { PROMO_LIST_GRID_PARAMS, PROMO_LIST_URL_PARAMS } from "ppt/constants";
import { isPromoTileFilter } from "ppt/utilities";
import { usePPTActions, usePPTState } from "tap/store/ppt/usePPT";

const URL_WRITE_DEBOUNCE_MS = 300;

/**
 * Hook to manage URL state synchronization for the promo list.
 * Handles bidirectional sync between URL params and Redux state.
 *
 * This is complex because:
 * - URL is source of truth on mount
 * - Redux drives grid updates during interaction
 * - Multiple state sources (search, tile filter, grid state) must stay in sync
 * - URL writes are debounced to avoid excessive navigation
 */
export const usePromoListUrlState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setPromosSearch,
    setPromosIssueFilter,
  } = usePPTActions();
  const { promosSearch, promosIssueFilter } = usePPTState();

  // Refs to hold latest values for URL writes without triggering re-renders
  const gridStateRef = useRef(null);
  const searchRef = useRef(promosSearch);
  const tileFilterRef = useRef(promosIssueFilter);
  const locationRef = useRef(location);
  const lastWrittenSearchRef = useRef(null);

  // Keep refs in sync with state
  searchRef.current = promosSearch;
  tileFilterRef.current = promosIssueFilter;
  locationRef.current = location;

  // Build complete URL params from current state
  const buildCurrentParams = useCallback(
    () => ({
      [PROMO_LIST_URL_PARAMS.SEARCH]: searchRef.current,
      [PROMO_LIST_URL_PARAMS.TILE]: tileFilterRef.current,
      ...gridStateToParams(gridStateRef.current, PROMO_LIST_GRID_PARAMS),
    }),
    [],
  );

  // Write current state to URL, skipping redundant navigations
  const writeUrl = useCallback(() => {
    const current = locationRef.current;
    const nextParams = buildCurrentParams();
    const nextSearch = buildSearchString(nextParams);

    if (
      nextSearch === current.search ||
      nextSearch === lastWrittenSearchRef.current
    ) {
      return;
    }

    lastWrittenSearchRef.current = nextSearch;
    updateSearchParams(current, navigate, nextParams);
  }, [navigate, buildCurrentParams]);

  // Debounced URL writer for high-frequency updates
  const debouncedWriteUrl = useRef(null);
  if (!debouncedWriteUrl.current) {
    debouncedWriteUrl.current = debounce(writeUrl, URL_WRITE_DEBOUNCE_MS);
  }

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedWriteUrl.current?.cancel(), []);

  // Seed Redux from URL on mount (URL is source of truth)
  const hasSeededFromUrl = useRef(false);
  useEffect(() => {
    if (hasSeededFromUrl.current) return;
    hasSeededFromUrl.current = true;

    const params = parseSearchParams(location.search);
    const urlSearch = params[PROMO_LIST_URL_PARAMS.SEARCH];
    const urlTile = params[PROMO_LIST_URL_PARAMS.TILE];

    const nextSearch = typeof urlSearch === "string" ? urlSearch : "";
    const nextTile = isPromoTileFilter(urlTile) ? urlTile : null;

    // Update refs immediately so writeUrl doesn't overwrite URL params
    searchRef.current = nextSearch;
    tileFilterRef.current = nextTile;

    // Dispatch to Redux if values differ
    if (nextSearch !== promosSearch) setPromosSearch(nextSearch);
    if (nextTile !== (promosIssueFilter ?? null))
      setPromosIssueFilter(nextTile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, setPromosSearch, setPromosIssueFilter]);

  // Sync tile filter changes to URL (skip the initial mount since we just seeded)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    writeUrl();
  }, [promosIssueFilter, writeUrl]);

  return {
    gridStateRef,
    searchRef,
    tileFilterRef,
    locationRef,
    writeUrl,
    debouncedWriteUrl,
    lastWrittenSearchRef,
    buildCurrentParams,
  };
};
